import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { usePhoenixRefresh } from './usePhoenixRefresh'
import { useQueueByTable } from './useQueueByTable'
import { enqueueMutation } from '../lib/mutationQueue'
import { getCached, setCached } from '../lib/hookCache'

const TABLE = 'weight_entries'
const CACHE_SCOPE = 'weight_entries'

export function useWeightEntries() {
  const { user } = useAuth()
  const cached = getCached(CACHE_SCOPE, user?.id)
  const [serverEntries, setServerEntries] = useState(cached || [])
  // Skip the initial loading flash when we already have cached data —
  // the screen shows last-known state immediately while the refetch
  // runs in the background.
  const [loading, setLoading] = useState(!cached)
  const [error, setError] = useState(null)
  const pending = useQueueByTable(TABLE)
  const initialLoadedRef = useRef(!!cached)

  const fetchEntries = useCallback(async () => {
    if (!supabaseConfigured) {
      setError('Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to your .env file.')
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from(TABLE)
      .select('*')
      .order('date', { ascending: false })

    if (err) {
      setError(err.message)
    } else {
      const next = data || []
      setServerEntries(next)
      if (user) setCached(CACHE_SCOPE, user.id, next)
    }
    initialLoadedRef.current = true
    setLoading(false)
  }, [user])

  useEffect(() => {
    fetchEntries()
  }, [fetchEntries])

  usePhoenixRefresh(fetchEntries)

  // Merge server entries with optimistic pending mutations
  const entries = useMemo(() => {
    let view = [...serverEntries]
    for (const m of pending) {
      if (m.op === 'upsert') {
        const idx = view.findIndex((e) => e.user_id === m.payload.user_id && e.date === m.payload.date)
        if (idx >= 0) {
          view[idx] = { ...view[idx], ...m.payload, _pending: m }
        } else {
          view.unshift({ id: m.id, ...m.payload, _pending: m })
        }
      } else if (m.op === 'update') {
        const idx = view.findIndex((e) => e.id === m.payload.id)
        if (idx >= 0) view[idx] = { ...view[idx], ...m.payload, _pending: m }
      } else if (m.op === 'delete') {
        view = view.filter((e) => e.id !== m.payload.id)
      }
    }
    // Re-sort by date desc since pending upserts may have been prepended
    view.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
    return view
  }, [serverEntries, pending])

  const upsertEntry = async (date, weight, note = null) => {
    if (!user) return false
    setError(null)
    await enqueueMutation({
      table: TABLE,
      op: 'upsert',
      payload: { date, weight, note: note || null, user_id: user.id },
      conflictKey: 'user_id,date',
      userId: user.id,
    })
    return true
  }

  const updateEntry = async (id, weight, note = null) => {
    if (!user) return false
    setError(null)
    await enqueueMutation({
      id,
      table: TABLE,
      op: 'update',
      payload: { id, weight, note: note || null },
      userId: user.id,
    })
    return true
  }

  const deleteEntry = async (id) => {
    if (!user) return false
    setError(null)
    await enqueueMutation({
      id,
      table: TABLE,
      op: 'delete',
      payload: { id },
      userId: user.id,
    })
    return true
  }

  return { entries, loading, error, upsertEntry, updateEntry, deleteEntry, refetch: fetchEntries }
}
